import styled, { css } from 'styled-components';
import colors from '@keyslabor/shared/theme/colors';
import { Row, Box, Button, Flex } from '@keyslabor/shared/ui';
import TryAgainPopup from './TryAgainPopup';
import theme from '@keyslabor/shared/theme';
import { useTranslation } from 'react-i18next';
import { CSSProperties, FC } from 'react';
import {
  VIDEO_MIN_LENGTH,
  VIDEO_MAX_LENGTH,
} from '@/pages/Answers/constants.ts';
import { cn } from '@/lib/utils';
import progressClasses from '@/components/radial-progress.module.css';

interface RecordButtonProps {
  isRecording: boolean;
  isRecordReady: boolean;
  onNextStep: () => void;
  onRecordStart: () => void;
  onRecordStop: () => void;
  onHideRetryConfirm: () => void;
  onShowRetryConfirm: () => void;
  isShowRetryConfirm: boolean;
  timeLeft: number;
  children?: React.ReactNode;
  onRetryRecord?: () => void;
}

type RecordingButtonProps = {
  isRecording: boolean;
  isRecordReady: boolean;
  isDisabled: boolean;
  onRecordStart: () => void;
  onRecordStop: () => void;
  onShowRetryConfirm: () => void;
  timeLeft: number;
};

const RecordingButtonLabel: FC<{
  isRecording: boolean;
  isRecordReady: boolean;
  timeLeft: number;
}> = ({ isRecording, isRecordReady, timeLeft }) => {
  const { t } = useTranslation();

  if (isRecording) {
    return (
      <span key={timeLeft}>
        {t('record_button.stop')}
        <br />
        <span>
          {timeLeft} {t('record_button.sec')}
        </span>
      </span>
    );
  }

  if (!isRecordReady) {
    return <>{t('record_button.start_record')}</>;
  }

  return (
    <Flex gap="0" flexDirection="column">
      <span style={{ height: '16px' }}>{t('record_button.try_again_ln1')}</span>
      <span>{t('record_button.try_again_ln2')}</span>
    </Flex>
  );
};

const StylesRecordingButton = styled.button<{
  $isTryAgain: boolean;
  $isRecording: boolean;
}>`
  border-radius: 100%;
  width: 100px;
  height: 100px;
  border-width: 4px;
  border-style: solid;
  border-color: white;
  cursor: pointer;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: white;

  background-color: ${({ $isTryAgain, $isRecording }) => {
    if ($isTryAgain) {
      return colors.blueDeep;
    }

    return $isRecording ? colors.redCrimson : colors.greenSummer;
  }};

  &[disabled] {
    background-color: ${colors.grayDisabled};
  }
`;

const RecordingButton: FC<RecordingButtonProps> = ({
  isRecording,
  isRecordReady,
  isDisabled,
  onRecordStart,
  onRecordStop,
  onShowRetryConfirm,
  timeLeft,
}) => {
  const isTryAgain = !isRecording && isRecordReady;

  const handlerClick = () => {
    if (isTryAgain) {
      onShowRetryConfirm();
    } else if (isRecording) {
      onRecordStop();
    } else {
      onRecordStart();
    }
  };

  return (
    <StylesRecordingButton
      $isRecording={isRecording}
      $isTryAgain={isTryAgain}
      onClick={handlerClick}
      disabled={isDisabled}
    >
      <Box
        textAlign="center"
        fontWeight="700"
        color="white"
        fontSize="16px"
        margin="0 auto"
        height="100%"
        css={css`
          & {
            display: -webkit-flex;
            display: flex;
            justify-content: center;
            -webkit-justify-content: center;
            align-items: center;
            -webkit-align-items: center;
          }
        `}
      >
        <RecordingButtonLabel
          isRecording={isRecording}
          isRecordReady={isRecordReady}
          timeLeft={timeLeft}
        />
      </Box>
    </StylesRecordingButton>
  );
};

export default function RecordButton({
  isRecordReady,
  isRecording,
  onNextStep,
  onRecordStart,
  onRecordStop,
  isShowRetryConfirm,
  onShowRetryConfirm,
  onHideRetryConfirm,
  timeLeft,
  children,
  onRetryRecord,
}: RecordButtonProps) {
  const isDisabled =
    isRecording && VIDEO_MAX_LENGTH - timeLeft < VIDEO_MIN_LENGTH;

  const progress = isDisabled ? Math.min(
    ((VIDEO_MAX_LENGTH - timeLeft + 1) / VIDEO_MIN_LENGTH) * 100,
    100
  ) : 0;
  return (
    <>
      <Row
        mt="20px"
        width="100%"
        position="absolute"
        backgroundColor="transparent"
        flexCenter
        height="140px"
        borderTopLeftRadius="20px"
        borderTopRightRadius="20px"
        bottom="0"
        zIndex={100}
      >
        <div
          className={cn(
            progressClasses['radial-progress'],
            progress === 0 ? progressClasses['reset'] : '',
            'absolute -top-8 text-[#569de5]'
          )}
          style={
            {
              '--value': progress,
              '--size': '100px',
              '--thickness': '5px',
            } as CSSProperties
          }
        >
          <RecordingButton
            isRecording={isRecording}
            isRecordReady={isRecordReady}
            isDisabled={isDisabled}
            onRecordStart={onRecordStart}
            onRecordStop={onRecordStop}
            onShowRetryConfirm={onShowRetryConfirm}
            timeLeft={timeLeft}
          />
        </div>
        <Button
          mt="60px"
          maxWidth="332px"
          kind="large"
          disabled={!isRecordReady}
          onClick={onNextStep}
          css={`
            &[disabled] {
              background-color: ${theme.colors.grayDisabled} !important;
            }
          `}
        >
          {children}
        </Button>
      </Row>
      <TryAgainPopup
        onHideRetryConfirm={onHideRetryConfirm}
        isShowRetryConfirm={isShowRetryConfirm}
        onRecordStart={() => {
          onRetryRecord?.();
        }}
      />
    </>
  );
}
